import { Link } from 'react-router-dom';
import { Mock } from '../components/Mock';
import { Section } from '../components/Section';
import { stayDate } from '../lib/format';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { EVENT, SEO } from '../seo/pages';

interface Faq {
  q: string;
  a: React.ReactNode;
}

/**
 * Rewritten on 2026-09-15 for the move to Secrets Hideaway. Everything the old
 * FAQ answered - check-in times, bed layouts, what to say at the front desk,
 * breakfast and the pool - belonged to a hotel that is no longer involved.
 *
 * The venue's own phone number is deliberately not published here. The number
 * in settings is the old hotel's until someone changes it, and a wrong number
 * next to the right venue name sends mourners to a front desk that knows
 * nothing about this.
 *
 * Two addresses on purpose (James, 2026-09-15). Questions about the night go to
 * the resort's inbox, which is what he asked for. Anything about a reservation
 * made on this site stays with the family's address: that is the merchant of
 * record for the refunds, and the resort cannot answer it.
 */
export function ContactPage() {
  usePageMeta(SEO.contact.title, SEO.contact.description, { jsonLd: SEO.contact.jsonLd });
  const { data: s } = useSettings();
  const venue = EVENT.venue.name;
  const address = `${EVENT.venue.street}, ${EVENT.venue.city}, ${EVENT.venue.region} ${EVENT.venue.postalCode}`;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const night = s?.eventStartDate ? stayDate(s.eventStartDate) : 'the night of the celebration';
  const link = 'text-pink-300 underline hover:text-pink-200';

  const faqs: Faq[] = [
    {
      q: 'Where is the celebration now?',
      a: (
        <>
          {venue}, {address}. Come in <strong>through the Tiki bar</strong>, on the white-fence
          side. That is the separate entrance for Brandon&apos;s Celebration of Life, not the main
          one. Security will be around to point people the right way.{' '}
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={link}>
            Get directions
          </a>
          .
        </>
      ),
    },
    {
      q: 'Why did it move?',
      a: (
        <>
          In the family&apos;s words: circumstances completely outside of their control, and
          misinformation about the nature of the event. The whole message is on the{' '}
          <Link to="/update" className={link}>
            update page
          </Link>
          . The date has not changed.
        </>
      ),
    },
    {
      q: 'What does it cost to get in?',
      a: 'Nothing between 7:00 and 10:00 PM. Entry is free in that window for everyone coming for Brandon, and you will be given a wristband for the Celebration of Life as you arrive.',
    },
    {
      q: 'Does it finish at ten?',
      a: 'No. Ten is when free entry stops, not when the night ends. To stay on after that you get another wristband from the Front Desk, and that one is not free.',
    },
    {
      q: 'Is there food?',
      a: 'Yes, there will be food.',
    },
    {
      q: 'Do I need to RSVP?',
      a: (
        <>
          Please do.{' '}
          <Link to="/rsvp" className={link}>
            RSVP here
          </Link>{' '}
          with the name of everyone in your party, and give your name at the door. A donation is
          welcome, never required, and all of it goes to Shawna.
        </>
      ),
    },
    {
      q: 'I booked a room on this site. What happens to it?',
      a: (
        <>
          That room was part of the block at the old hotel, and the block is gone. Every reservation
          made here is being refunded to the card that paid for it and there is nothing you need to
          do.
          {s?.contactEmail ? ` Questions about yours: ${s.contactEmail}.` : ''}
        </>
      ),
    },
    {
      q: 'Where can I stay instead?',
      a: (
        <>
          {venue} is sold out.{' '}
          <Link to="/rooms" className={link}>
            Where to stay
          </Link>{' '}
          sends you to SecretsOverflow, the resort&apos;s own overflow booking site, for rooms at
          hotels nearby; the closest is about a third of a mile up the road. Book{' '}
          {night === 'the night of the celebration' ? night : `the night of ${night}`}.
        </>
      ),
    },
    {
      q: 'What should I wear?',
      a: 'Rockstar. Come dressed for the show, not the service: leather and denim, band tees, boots, studs, sunglasses at night. Leave the suits and the ties at home.',
    },
    {
      q: 'I cannot come. How else can I help?',
      a: (
        <>
          Give through the family GoFundMe on the{' '}
          <Link to="/donate" className={link}>
            Donate
          </Link>{' '}
          page, or leave something for the family in the{' '}
          <Link to="/guestbook" className={link}>
            guestbook
          </Link>
          . Sharing the site helps too.
        </>
      ),
    },
  ];

  return (
    <Section eyebrow="Get in touch" title="Contact & FAQ">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
        <div className="flex flex-col gap-8">
          <div>
            <p className="text-meta text-sm">Questions about the night</p>
            <a
              href={`mailto:${EVENT.questionsEmail}`}
              className="mt-1 block text-xl text-ink-50 hover:text-pink-300"
            >
              {EVENT.questionsEmail}
            </a>
            <p className="mt-2 text-ink-400">
              Getting in, the wristbands, anything about the celebration itself.
            </p>
          </div>
          <div>
            <p className="text-meta text-sm">A room you booked on this site</p>
            {s?.contactEmail ? (
              <a
                href={`mailto:${s.contactEmail}`}
                className="mt-1 block text-xl text-ink-50 hover:text-pink-300"
              >
                {s.contactEmail}
              </a>
            ) : (
              <Mock className="mt-2">
                <p className="text-xl text-ink-50">family@brandonstrong.com</p>
              </Mock>
            )}
            <p className="mt-2 text-ink-400">
              Reservations made here and the refunds going back out. The venue cannot help with
              those.
            </p>
          </div>
          <div>
            <p className="text-meta text-sm">The venue</p>
            <p className="mt-1 text-xl text-ink-50">{venue}</p>
            <p className="text-ink-200">{address}</p>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-2 inline-block text-sm ${link}`}
            >
              Get directions
            </a>
            <p className="mt-2 text-ink-400">
              Come in through the Tiki bar, on the white-fence side.
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-3xl">Questions</h2>
          <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
            {faqs.map((f) => (
              <details key={f.q} className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-sans text-lg font-semibold text-ink-50 marker:content-none">
                  {f.q}
                  <span
                    className="text-pink-500 transition-transform group-open:rotate-45"
                    aria-hidden
                  >
                    +
                  </span>
                </summary>
                <div className="faq-answer pt-3 text-ink-200">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
